import React, { useState, useEffect, useRef } from 'react';
import { buscarClientes, getClientePorCuil, revelarClaves } from '../../services/clienteService';
import ansesConexionService from '../../services/ansesConexionService';
import { obtenerExpedientesPorCliente } from '../../services/seguimientoExpedientesService';
import { PRIORIDADES } from '../../constants/prioridadAnses';
import '../../style/components/ANSESLoginModal.css';

const POLL_CONEXION_MS = 4000;
const MAX_ESPERA_CONEXION_MS = 6 * 60 * 1000; // 2captcha + reintentos del backend pueden tardar varios minutos
const POLL_BUSQUEDA_MS = 15000; // la sync post-login puede tardar hasta ~2 min en tener datos
const MAX_INTENTOS_BUSQUEDA = 8;

// El CUIL se guarda siempre sin guiones (cliente y seguimiento, V262); con guiones solo se muestra.
export const formatearCuil = (cuilRaw) => {
    const digitos = String(cuilRaw || '').replace(/\D/g, '');
    if (digitos.length !== 11) return cuilRaw;
    return `${digitos.slice(0, 2)}-${digitos.slice(2, 10)}-${digitos.slice(10)}`;
};

const inicialesDe = (nombre, apellido) =>
    `${(apellido || '?').charAt(0)}${(nombre || '').charAt(0)}`.toUpperCase();

// Relleno visual fijo — no representa el largo real de la clave guardada.
const CLAVE_DUMMY = '••••••••••';

// onRefrescar() se llama apenas se agrega y de nuevo cuando termina de conectar, para reflejar el estado final.
// clientePreseleccionado ({ cuil, cuilRaw, nombre }) salta el paso de búsqueda — lo usa el botón
// "Conectar ANSES" de la ficha del cliente, donde el cliente ya se conoce de antemano.
function ModalAgregarClienteAnses({ open, onClose, onAgregar, onRefrescar, clientePreseleccionado = null }) {
    const [paso, setPaso] = useState('buscar'); // 'buscar' | 'detalles' | 'conectando'
    const [criterio, setCriterio] = useState('');
    const [resultados, setResultados] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [clave, setClave] = useState('');
    const [claveVisible, setClaveVisible] = useState(false); // toggle mostrar/ocultar el texto tipeado
    const [claveYaGuardada, setClaveYaGuardada] = useState(false); // ya conectado antes, no hace falta re-pedir la clave
    const [claveClienteGuardada, setClaveClienteGuardada] = useState(false); // clave de la ficha del cliente (fallback), distinta de la de arriba
    const [mostrandoValorReal, setMostrandoValorReal] = useState(false); // true tras revelar, o si el usuario elige escribir una nueva
    const [mostrarRevelar, setMostrarRevelar] = useState(false);
    const [passwordRevelar, setPasswordRevelar] = useState('');
    const [revelando, setRevelando] = useState(false);
    const [errorRevelar, setErrorRevelar] = useState('');
    const [prioridad, setPrioridad] = useState('MEDIA');
    const [enviando, setEnviando] = useState(false);
    const [errorEnvio, setErrorEnvio] = useState('');
    // null (conectando) | 'buscando' (login OK, esperando expedientes) | 'exitoso' | 'error'
    const [estadoConexion, setEstadoConexion] = useState(null);
    const [mensajeErrorConexion, setMensajeErrorConexion] = useState('');
    const debounceRef = useRef(null);
    const pollRef = useRef(null);
    const timerCierreRef = useRef(null);

    const limpiarPoll = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (timerCierreRef.current) { clearTimeout(timerCierreRef.current); timerCierreRef.current = null; }
    };

    // Dos fuentes distintas de "ya hay clave": la sesión ya conectada en servicioAnses, y la
    // Clave de Mi ANSES cargada en la ficha del cliente (se usa como fallback al conectar, ver
    // AnsesConexionController.iniciar() / SeguimientoExpedientesService.agregarALista()).
    const cargarEstadoClave = async (seleccionado) => {
        setClaveYaGuardada(false);
        setClaveClienteGuardada(false);
        setMostrandoValorReal(false);
        const [estadoAnses, clienteDetalle] = await Promise.all([
            ansesConexionService.obtenerEstado(seleccionado.cuil).catch(() => null),
            getClientePorCuil(seleccionado.cuilRaw).catch(() => null),
        ]);
        setClaveYaGuardada(!!estadoAnses?.tieneClaveGuardada);
        setClaveClienteGuardada(!!clienteDetalle?.clave?.claveSs);
    };

    useEffect(() => {
        if (!open) {
            limpiarPoll();
            setPaso('buscar');
            setCriterio('');
            setResultados([]);
            setClienteSeleccionado(null);
            setClave('');
            setClaveVisible(false);
            setClaveYaGuardada(false);
            setClaveClienteGuardada(false);
            setMostrandoValorReal(false);
            setMostrarRevelar(false);
            setPasswordRevelar('');
            setErrorRevelar('');
            setPrioridad('MEDIA');
            setEnviando(false);
            setErrorEnvio('');
            setEstadoConexion(null);
            setMensajeErrorConexion('');
            return;
        }
        if (clientePreseleccionado) {
            setClienteSeleccionado(clientePreseleccionado);
            setPaso('detalles');
            cargarEstadoClave(clientePreseleccionado);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, clientePreseleccionado?.cuilRaw]);

    useEffect(() => () => limpiarPoll(), []);

    // Tras el login, poll-ea hasta que aparezcan expedientes (o se agoten los intentos) y cierra el modal solo.
    const pollearExpedientes = (cuil) => {
        let intentos = 0;
        pollRef.current = setInterval(async () => {
            intentos += 1;
            try {
                const data = await obtenerExpedientesPorCliente(cuil);
                if (data.length === 0 && intentos < MAX_INTENTOS_BUSQUEDA) return;
            } catch (_) {
                if (intentos < MAX_INTENTOS_BUSQUEDA) return;
            }
            limpiarPoll();
            setEstadoConexion('exitoso');
            onRefrescar?.();
            timerCierreRef.current = setTimeout(() => onClose(), 1500);
        }, POLL_BUSQUEDA_MS);
    };

    // Poll-ea el estado hasta ACTIVE (pasa a buscar expedientes) o ERROR (queda listo para VNC manual).
    const pollearConexion = (cuil) => {
        const inicio = Date.now();
        pollRef.current = setInterval(async () => {
            try {
                const data = await ansesConexionService.obtenerEstado(cuil);
                if (data?.estado === 'ACTIVE') {
                    limpiarPoll();
                    setEstadoConexion('buscando');
                    onRefrescar?.();
                    pollearExpedientes(cuil);
                } else if (data?.estado === 'ERROR') {
                    limpiarPoll();
                    setEstadoConexion('error');
                    setMensajeErrorConexion(
                        data?.mensajeError
                            ? `No se pudo conectar automáticamente: ${data.mensajeError}`
                            : 'No se pudo conectar automáticamente a ANSES.'
                    );
                    onRefrescar?.();
                } else if (Date.now() - inicio > MAX_ESPERA_CONEXION_MS) {
                    limpiarPoll();
                    setEstadoConexion('error');
                    setMensajeErrorConexion('La conexión automática está tardando demasiado.');
                    onRefrescar?.();
                }
            } catch (_) {
                // error de red puntual, se reintenta en el próximo tick
            }
        }, POLL_CONEXION_MS);
    };

    // Misma confirmación de contraseña propia que usa la ficha del cliente para AFIP/CIDI —
    // reusa /api/clientes/{cuil}/revelar-claves, no hace falta nada nuevo en el backend.
    const confirmarRevelarClave = async () => {
        if (!passwordRevelar) {
            setErrorRevelar('Ingresá tu contraseña');
            return;
        }
        setRevelando(true);
        setErrorRevelar('');
        try {
            const data = await revelarClaves(clienteSeleccionado.cuilRaw, passwordRevelar);
            setClave(data?.claveSs || '');
            setClaveVisible(true);
            setMostrandoValorReal(true);
            setMostrarRevelar(false);
            setPasswordRevelar('');
        } catch (e) {
            // Spring Security devuelve 403 tanto si falta el rol requerido como si la
            // contraseña es incorrecta; se distinguen por el cuerpo de la respuesta
            // (el accessDeniedHandler global devuelve {error: "Acceso denegado", ...}).
            const data = e?.response?.data;
            if (data && typeof data === 'object' && data.error === 'Acceso denegado') {
                setErrorRevelar('Tu rol de usuario no tiene permiso para revelar esta clave.');
            } else {
                setErrorRevelar('Contraseña incorrecta.');
            }
        } finally {
            setRevelando(false);
        }
    };

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!criterio.trim()) {
            setResultados([]);
            return undefined;
        }
        debounceRef.current = setTimeout(async () => {
            setBuscando(true);
            try {
                const data = await buscarClientes(criterio.trim());
                setResultados(Array.isArray(data) ? data : []);
            } catch (e) {
                setResultados([]);
            } finally {
                setBuscando(false);
            }
        }, 350);
        return () => clearTimeout(debounceRef.current);
    }, [criterio]);

    if (!open) return null;

    const mostrandoDummy = claveClienteGuardada && !mostrandoValorReal;

    return (
        <div className="anses-modal-overlay" role="dialog" aria-modal="true" aria-label="Agregar cliente a la lista">
            <div className="anses-modal-content">
                <div className="anses-modal-header">
                    <h4>
                        <i className="bi bi-person-plus-fill me-2" />
                        {clientePreseleccionado ? 'Conectar ANSES' : 'Agregar cliente a la lista'}
                    </h4>
                    <button
                        type="button"
                        aria-label="Cerrar"
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#64748b',
                            padding: '0.25rem',
                            lineHeight: 1,
                            fontSize: '1.25rem',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <i className="bi bi-x-lg" />
                    </button>
                </div>
                <div className="anses-modal-body">
                    {paso === 'buscar' && (
                        <>
                            <div className="input-group mb-3">
                                <span className="input-group-text" style={{ background: 'transparent', border: '1px solid #dee2e6', borderRight: 'none' }}>
                                    <i className="bi bi-search" style={{ color: '#6B7280' }} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Buscar por nombre, apellido o CUIL…"
                                    value={criterio}
                                    onChange={(e) => setCriterio(e.target.value)}
                                    autoFocus
                                    style={{ borderLeft: 'none', fontSize: '1rem' }}
                                />
                            </div>

                            {buscando && (
                                <div className="text-center py-4">
                                    <div className="spinner-border spinner-border-sm text-primary" />
                                    <p className="mt-2 mb-0" style={{ color: '#6B7280', fontSize: 13 }}>Buscando…</p>
                                </div>
                            )}

                            {!buscando && criterio.trim() && resultados.length === 0 && (
                                <div className="text-center py-4">
                                    <i className="bi bi-search" style={{ fontSize: '1.75rem', color: '#cbd5e1' }} />
                                    <p className="mt-2 mb-0" style={{ color: '#64748b', fontSize: 13 }}>No se encontraron clientes.</p>
                                </div>
                            )}

                            {!buscando && !criterio.trim() && (
                                <div className="text-center py-4">
                                    <i className="bi bi-people" style={{ fontSize: '1.75rem', color: '#cbd5e1' }} />
                                    <p className="mt-2 mb-0" style={{ color: '#64748b', fontSize: 13 }}>
                                        Escribí para buscar entre todos los clientes del estudio.
                                    </p>
                                </div>
                            )}

                            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                                {resultados.map((c) => (
                                    <button
                                        type="button"
                                        key={c.cuil}
                                        className="anses-cliente-resultado"
                                        onClick={async () => {
                                            const seleccionado = { cuil: c.cuil, cuilRaw: c.cuil, nombre: `${c.apellido} ${c.nombre}` };
                                            setClienteSeleccionado(seleccionado);
                                            setPaso('detalles');
                                            await cargarEstadoClave(seleccionado);
                                        }}
                                    >
                                        <span className="anses-cliente-avatar">{inicialesDe(c.nombre, c.apellido)}</span>
                                        <span className="anses-cliente-info">
                                            <span className="anses-cliente-nombre">{c.apellido} {c.nombre}</span>
                                            <span className="anses-cliente-cuil">{formatearCuil(c.cuil)}</span>
                                        </span>
                                        <i className="bi bi-plus-circle-fill anses-cliente-add" />
                                    </button>
                                ))}
                            </div>
                        </>
                    )}

                    {paso === 'detalles' && clienteSeleccionado && (
                        <>
                            <p className="anses-modal-help">
                                <strong>{clienteSeleccionado.nombre}</strong>
                                <span className="text-muted" style={{ fontSize: 13 }}> — CUIL {formatearCuil(clienteSeleccionado.cuil)}</span>
                            </p>

                            <label className="form-label" >
                                Clave de Mi ANSES {(claveYaGuardada || claveClienteGuardada) ? '' : '(opcional)'}
                            </label>
                            {claveYaGuardada && (
                                <p style={{ fontSize: 12, color: '#166534', marginBottom: 8 }}>
                                    <i className="bi bi-check-circle-fill me-1" />
                                    Ya hay una clave guardada para este cliente — se va a usar para conectar automáticamente.
                                </p>
                            )}
                            {!claveYaGuardada && claveClienteGuardada && (
                                <p style={{ fontSize: 12, color: '#0f766e', marginBottom: 8 }}>
                                    <i className="bi bi-info-circle-fill me-1" />
                                    Este cliente ya tiene una Clave de Mi ANSES cargada en su ficha — se va a usar
                                    como respaldo al conectar. Tocá el ojo para verla.
                                </p>
                            )}
                            {mostrarRevelar && (
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                                    <p style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
                                        Confirmá tu contraseña para ver la clave guardada:
                                    </p>
                                    <input
                                        type="password"
                                        className="form-control form-control-sm mb-2"
                                        placeholder="Tu contraseña"
                                        value={passwordRevelar}
                                        onChange={(e) => setPasswordRevelar(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && confirmarRevelarClave()}
                                        autoFocus
                                    />
                                    {errorRevelar && (
                                        <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>{errorRevelar}</p>
                                    )}
                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => { setMostrarRevelar(false); setPasswordRevelar(''); setErrorRevelar(''); }}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary flex-fill"
                                            disabled={revelando}
                                            onClick={confirmarRevelarClave}
                                        >
                                            {revelando ? 'Verificando…' : 'Confirmar'}
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className="input-group mb-2">
                                <input
                                    type={mostrandoDummy ? 'password' : (claveVisible ? 'text' : 'password')}
                                    className="form-control"
                                    placeholder={(claveYaGuardada || claveClienteGuardada) ? 'Dejalo vacío para usar la ya guardada, o cargá una nueva' : 'Si la cargás, el sistema intenta conectarse solo'}
                                    value={mostrandoDummy ? CLAVE_DUMMY : clave}
                                    readOnly={mostrandoDummy}
                                    onChange={(e) => { if (!mostrandoDummy) setClave(e.target.value); }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    tabIndex={-1}
                                    onClick={() => {
                                        if (mostrandoDummy) {
                                            setMostrarRevelar(true);
                                        } else {
                                            setClaveVisible((v) => !v);
                                        }
                                    }}
                                    aria-label={mostrandoDummy ? 'Ver clave guardada' : (claveVisible ? 'Ocultar clave' : 'Mostrar clave')}
                                >
                                    <i className={`bi ${!mostrandoDummy && claveVisible ? 'bi-eye-slash' : 'bi-eye'}`} />
                                </button>
                            </div>
                            {mostrandoDummy && (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 mb-2"
                                    style={{ fontSize: 12 }}
                                    onClick={() => { setMostrandoValorReal(true); setClave(''); }}
                                >
                                    Escribir una clave nueva
                                </button>
                            )}
                            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
                                {(claveYaGuardada || claveClienteGuardada)
                                    ? 'Podés dejarlo vacío para reusar la clave guardada, o escribir una nueva si cambió.'
                                    : 'Sin clave, el cliente queda en la lista listo para conectar más tarde desde su detalle.'}
                            </p>

                            <label className="form-label" >
                                Prioridad de seguimiento
                            </label>
                            <div className="d-flex gap-2 mb-1">
                                {PRIORIDADES.map((p) => (
                                    <button
                                        key={p.valor}
                                        type="button"
                                        onClick={() => setPrioridad(p.valor)}
                                        style={{
                                            flex: 1,
                                            padding: '0.5rem',
                                            borderRadius: 8,
                                            border: `2px solid ${prioridad === p.valor ? '#3b82f6' : '#cbd5e1'}`,
                                            background: prioridad === p.valor ? '#eff6ff' : '#f8fafc',
                                            color: prioridad === p.valor ? '#1e40af' : '#64748b',
                                            fontWeight: prioridad === p.valor ? 700 : 400,
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 18 }}>
                                {PRIORIDADES.find((p) => p.valor === prioridad)?.ayuda}. La prioridad que elijas acá
                                queda para todos los usuarios.
                            </p>

                            {errorEnvio && (
                                <div className="alert alert-danger" style={{ fontSize: 13, marginBottom: 14 }}>{errorEnvio}</div>
                            )}

                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => {
                                        if (clientePreseleccionado) {
                                            onClose();
                                            return;
                                        }
                                        setPaso('buscar');
                                        setClienteSeleccionado(null);
                                        setClave('');
                                        setClaveVisible(false);
                                        setClaveClienteGuardada(false);
                                        setMostrandoValorReal(false);
                                        setMostrarRevelar(false);
                                        setPasswordRevelar('');
                                        setErrorRevelar('');
                                    }}
                                    disabled={enviando}
                                >
                                    {clientePreseleccionado ? 'Cancelar' : (
                                        <>
                                            <i className="bi bi-arrow-left me-2" />
                                            Volver
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary flex-fill"
                                    disabled={enviando}
                                    onClick={async () => {
                                        setEnviando(true);
                                        setErrorEnvio('');
                                        try {
                                            await onAgregar({ cuil: clienteSeleccionado.cuil, clave, prioridad });
                                            onRefrescar?.();
                                            if (clave || claveYaGuardada || claveClienteGuardada) {
                                                setPaso('conectando');
                                                pollearConexion(clienteSeleccionado.cuil);
                                            } else {
                                                onClose();
                                            }
                                        } catch (e) {
                                            setErrorEnvio('No se pudo agregar el cliente. Intentá de nuevo.');
                                        } finally {
                                            setEnviando(false);
                                        }
                                    }}
                                >
                                    {enviando ? (
                                        <span className="spinner-border spinner-border-sm me-2" />
                                    ) : (
                                        <i className="bi bi-plus-circle me-2" />
                                    )}
                                    {clave || claveYaGuardada || claveClienteGuardada ? 'Agregar y conectar' : 'Agregar a la lista'}
                                </button>
                            </div>
                        </>
                    )}

                    {paso === 'conectando' && (
                        <div className="anses-loader-block">
                            {estadoConexion === null && (
                                <>
                                    <div className="spinner-border text-primary" role="status" />
                                    <p style={{ fontWeight: 500, marginTop: 10, marginBottom: 4 }}>Conectando con ANSES…</p>
                                    <small style={{ color: '#64748b', fontSize: 12, display: 'block' }}>
                                        Puede tardar unos minutos — estamos resolviendo el captcha automáticamente.
                                        Podés cerrar esta ventana, el cliente ya quedó en la lista y la conexión
                                        sigue en segundo plano.
                                    </small>
                                </>
                            )}
                            {estadoConexion === 'buscando' && (
                                <>
                                    <div className="spinner-border text-primary" role="status" />
                                    <p style={{ fontWeight: 500, marginTop: 10, marginBottom: 4 }}>Buscando expedientes…</p>
                                    <small style={{ color: '#64748b', fontSize: 12, display: 'block' }}>
                                        Conectado — ahora estamos revisando los trámites del cliente en ANSES.
                                        Puede demorar unos minutos; esta ventana se cierra sola cuando termine.
                                    </small>
                                </>
                            )}
                            {estadoConexion === 'exitoso' && (
                                <>
                                    <i className="bi bi-check-circle-fill" style={{ fontSize: 32, color: '#166534' }} />
                                    <p style={{ marginTop: 10 }}>Listo — ya está en la lista.</p>
                                </>
                            )}
                            {estadoConexion === 'error' && (
                                <>
                                    <div className="alert alert-danger" style={{ marginBottom: 12 }}>{mensajeErrorConexion}</div>
                                    <p className="anses-modal-help">
                                        No pudimos conectarnos solos. El cliente ya quedó en la lista — podés
                                        completar el login manualmente por VNC desde su detalle.
                                    </p>
                                    <button type="button" className="btn btn-primary w-100" style={{ marginTop: 12 }} onClick={onClose}>
                                        Entendido
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ModalAgregarClienteAnses;
